/**
 * «Подтянуть нормативы урожайности» — кнопка + подтверждение (AK-778).
 *
 * Макет (утверждён founder 28.08, ОБА варианта размещения):
 *   `docs/superpowers/prototypes/season-planning-2027/08-apply-yield-defaults.html`
 *
 * Оба варианта макета реализованы и не спорят друг с другом:
 *   • `tone="toolbar"` — кнопка над таблицей, на весь вариант плана (вариант А);
 *   • `tone="row"` — та же кнопка в строке культуры сводки СПП (вариант Б),
 *     сразу с числом пустых строк по ЭТОЙ культуре.
 * Ручка одна и та же, разница только в том, чем предзаполнен выбор охвата.
 *
 * Три вещи, которые экран обязан сказать до нажатия:
 *   • сколько строк сейчас без урожайности (счётчик на кнопке) — иначе кнопка
 *     обещает неизвестно что;
 *   • что руками введённые цифры не тронут — иначе действие читается как
 *     разрушительное, и им не пользуются;
 *   • что у части культур норматива нет и они останутся пустыми — иначе
 *     «заполнили не всё» выглядит поломкой.
 *
 * Слов «применить», «default», «унаследовано» на экране нет: у агронома это
 * «норматив хозяйства», «ваша цифра», «норматива нет».
 */
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useApplyYieldDefaults, type VariantField } from '@/lib/planning/planCrops';
import {
  cropsWithoutDefault,
  emptyYieldRows,
  listCropNames,
  scopeAllHint,
  scopeCropHint,
  yieldGapsByCrop,
} from '@/lib/planning/applyYieldDefaults';
import { cn } from '@/lib/utils';

export function ApplyYieldDefaultsButton({
  scenarioId,
  fields,
  canUpdate,
  scopeCropId = null,
  tone = 'toolbar',
  className,
}: {
  scenarioId: string;
  /** ВСЕ поля варианта: счётчик обязан считать вариант, а не то, что под фильтром. */
  fields: readonly VariantField[];
  /** Право на посевы (`sowings:U`) — его же спрашивает ручка. */
  canUpdate: boolean;
  /** Культура, из строки которой нажали (вариант Б макета) либо активный фильтр. */
  scopeCropId?: string | null;
  tone?: 'toolbar' | 'row';
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<'all' | 'crop'>('all');
  const apply = useApplyYieldDefaults();

  const gaps = yieldGapsByCrop(fields);
  const total = emptyYieldRows(gaps);
  const cropGap = scopeCropId ? (gaps.find((g) => g.cropId === scopeCropId) ?? null) : null;
  const noDefault = cropsWithoutDefault(gaps);

  // Число на кнопке — то, что кнопка реально заденет: в строке культуры это
  // пустые строки ЭТОЙ культуры, над таблицей — всего варианта.
  const count = tone === 'row' ? (cropGap?.emptyRows ?? 0) : total;

  if (!canUpdate) return null;
  // В строке культуры кнопки без пустых строк просто нет: строка и так с
  // урожайностью, а выключенная кнопка в каждой строке — шум.
  if (tone === 'row' && count === 0) return null;

  // Норматива у культуры нет — подтягивать нечего, и кнопка обязана сказать
  // ПОЧЕМУ. Выключенная кнопка без объяснения читается как поломка.
  const cropHasNoDefault = tone === 'row' && cropGap !== null && cropGap.defaultCHa === null;

  const openDialog = () => {
    setScope(cropGap ? 'crop' : 'all');
    setOpen(true);
  };

  const submit = () => {
    const cropId = scope === 'crop' && cropGap ? cropGap.cropId : null;
    apply.mutate(
      {
        scenarioId,
        cropId,
        // Имена культур без норматива — на момент нажатия: после применения
        // строки перечитаются, и назвать их в итоге будет уже нечем.
        gapsWithoutDefault: cropId
          ? noDefault.filter((g) => g.cropId === cropId)
          : noDefault,
      },
      { onSuccess: () => setOpen(false) },
    );
  };

  return (
    <>
      {cropHasNoDefault ? (
        <span className={cn('text-xs text-muted-foreground', className)}>
          Подтягивать нечего — норматив урожайности этой культуре не заведён
        </span>
      ) : (
        <Button
          type="button"
          size="sm"
          variant={tone === 'row' ? 'outline' : 'default'}
          className={className}
          disabled={count === 0 || apply.isPending}
          title={
            count === 0
              ? 'Все строки плана уже с урожайностью — подтягивать нечего'
              : undefined
          }
          onClick={openDialog}
        >
          {apply.isPending
            ? 'Заполняем…'
            : tone === 'row'
              ? 'Подтянуть норматив в пустые'
              : 'Подтянуть нормативы урожайности'}
          {count > 0 && (
            <Badge variant="secondary" className="ml-2">
              {count}
            </Badge>
          )}
        </Button>
      )}

      <Dialog open={open} onOpenChange={(o) => !o && !apply.isPending && setOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Подтянуть нормативы урожайности</DialogTitle>
            <DialogDescription>
              Заполним урожайность из нормативов хозяйства там, где она сейчас пустая.
              Цифры, которые вы вводили руками, останутся как есть — их не тронем.
            </DialogDescription>
          </DialogHeader>

          <RadioGroup
            value={scope}
            onValueChange={(v) => setScope(v === 'crop' ? 'crop' : 'all')}
            aria-label="На что подтянуть нормативы"
          >
            <label
              htmlFor="yield-scope-all"
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-lg border p-3',
                scope === 'all' ? 'border-primary bg-primary/5' : 'border-border',
              )}
            >
              <RadioGroupItem value="all" id="yield-scope-all" className="mt-1" />
              <span>
                <Label htmlFor="yield-scope-all" className="cursor-pointer">
                  Весь вариант плана
                </Label>
                <span className="block text-xs text-muted-foreground">
                  {scopeAllHint(gaps)}
                </span>
              </span>
            </label>

            {cropGap && (
              <label
                htmlFor="yield-scope-crop"
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-lg border p-3',
                  scope === 'crop' ? 'border-primary bg-primary/5' : 'border-border',
                )}
              >
                <RadioGroupItem value="crop" id="yield-scope-crop" className="mt-1" />
                <span>
                  <Label htmlFor="yield-scope-crop" className="cursor-pointer">
                    Только {cropGap.cropName.toLowerCase()}
                  </Label>
                  <span className="block text-xs text-muted-foreground">
                    {scopeCropHint(cropGap)}
                  </span>
                </span>
              </label>
            )}
          </RadioGroup>

          {/* Говорим ДО нажатия, что часть строк останется пустой: «заполнили
              не всё» без предупреждения читается как сбой действия. */}
          {noDefault.length > 0 && (scope === 'all' || cropGap?.defaultCHa === null) && (
            <p className="rounded-lg bg-muted p-3 text-xs text-muted-foreground">
              Норматив урожайности не заведён у культур: {listCropNames(noDefault)}. Их
              строки останутся пустыми — по ним урожайность вписывается прямо в строке
              поля. После заполнения покажем их отдельно.
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={apply.isPending}
              onClick={() => setOpen(false)}
            >
              Отмена
            </Button>
            <Button type="button" disabled={apply.isPending} onClick={submit}>
              {apply.isPending ? 'Заполняем…' : 'Заполнить урожайность'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
